/**
 * Password Validation Utilities
 * Provides password strength checking and validation
 */

export interface PasswordStrength {
  score: number; // 0-4 (0 = very weak, 4 = very strong)
  label: 'Very Weak' | 'Weak' | 'Fair' | 'Strong' | 'Very Strong';
  color: string;
  feedback: string[];
  isValid: boolean;
}

/**
 * Calculates password strength score and provides feedback
 * @param password - The password to analyze
 * @returns Password strength details
 */
export function calculatePasswordStrength(password: string): PasswordStrength {
  if (!password || password.length === 0) {
    return {
      score: 0,
      label: 'Very Weak',
      color: '#ef4444',
      feedback: ['Password is required'],
      isValid: false
    };
  }

  let score = 0;
  const feedback: string[] = [];

  // Length check (0-2 points)
  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('Use at least 8 characters');
  }

  if (password.length >= 12) {
    score += 1;
  } else if (password.length >= 8) {
    feedback.push('Longer passwords are more secure');
  }

  // Character variety checks (1 point each)
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  if (hasLowercase && hasUppercase) {
    score += 1;
  } else {
    feedback.push('Use both uppercase and lowercase letters');
  }

  if (hasNumbers) {
    score += 0.5;
  } else {
    feedback.push('Add numbers for better security');
  }

  if (hasSpecialChars) {
    score += 0.5;
  } else {
    feedback.push('Add special characters (!@#$%^&*)');
  }

  // Bonus point for having all character types
  if (hasLowercase && hasUppercase && hasNumbers && hasSpecialChars) {
    score += 1;
  }

  // Normalize score to 0-4 range
  score = Math.min(4, Math.max(0, Math.round(score)));

  // Determine label and color
  let label: PasswordStrength['label'];
  let color: string;
  let isValid = false;

  switch (score) {
    case 0:
      label = 'Very Weak';
      color = '#ef4444'; // red
      break;
    case 1:
      label = 'Weak';
      color = '#f97316'; // orange
      break;
    case 2:
      label = 'Fair';
      color = '#eab308'; // yellow
      isValid = true;
      break;
    case 3:
      label = 'Strong';
      color = '#10b981'; // green
      isValid = true;
      break;
    case 4:
      label = 'Very Strong';
      color = '#059669'; // dark green
      isValid = true;
      break;
    default:
      label = 'Very Weak';
      color = '#ef4444';
  }

  return {
    score,
    label,
    color,
    feedback: feedback.length > 0 ? feedback : ['Great password!'],
    isValid
  };
}
